import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild
} from '@angular/core';
import { Store } from '@ngrx/store';
import { isValid } from 'date-fns';
import { forEach as _forEach, has as _has } from 'lodash';
import { BehaviorSubject, combineLatest, filter, Observable, Subject, takeUntil } from 'rxjs';
import { map, take } from 'rxjs/operators';

import { AutoPaySelectors } from '@amfam/billing/auto-pay/data-access';
import { AutoPayTableRow, DatatableColumn } from '@amfam/billing/auto-pay/detail/models-util';
import { AutoPaySetupWrapperComponent, AutoPayUtilService } from '@amfam/billing/auto-pay/feature';
import { AutoPayUtilSelectors } from '@amfam/billing/auto-pay/util';
import { BillAccountsSelectors } from '@amfam/billing/billaccount/data-access';
import {
  PaymentMethodActions,
  PaymentMethodSelectors
} from '@amfam/billing/payment-method/data-access';
import {
  PaymentMethod,
  PaymentMethodAccountType,
  PaymentMethodAddEditConfig,
  PaymentMethodAddEditOperationType
} from '@amfam/billing/payment-method/util';
import { PolicySelectors } from '@amfam/policy/data-access';
import { AnalyticsFacade, AutomaticPaymentsAnalytics } from '@amfam/shared/analytics';
import {
  AutomaticPayment,
  BillAccount,
  BillingPaymentPaths,
  MultiAutopaySetupAnalytics
} from '@amfam/shared/models';
import { CurrencyUSDPipe, DateFormatPipe } from '@amfam/shared/ui/pipes';
import { FeatureFlagService } from '@amfam/shared/utility/feature-flag/data-access';
import { fromRouterActions } from '@amfam/shared/utility/navigation';
import { UtilService } from '@amfam/shared/utility/shared-services';
import { DsModalService, LoadingSpinnerService, ToasterService } from '@amfam/ui-kit';

import { AutoPayDeleteUtilService } from '../../util/auto-pay-delete-util.service';

@Component({
  selector: 'ds-auto-pay-detail-wrapper',
  templateUrl: './auto-pay-detail-wrapper.component.html',
  styleUrls: ['./auto-pay-detail-wrapper.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class AutoPayDetailWrapperComponent implements OnInit, OnChanges, OnDestroy {
  @Input() billAccountNumber: string;
  @Input() tableOnly = false;
  @Input() displayEnrolledTiles = false;

  // Table cell templates
  @ViewChild('headerCellTpl', { static: true }) headerCellTpl: TemplateRef<unknown>;
  @ViewChild('billAccountNameCellTpl', { static: true })
  billAccountNameCellTpl: TemplateRef<unknown>;
  @ViewChild('paymentAccountCellTpl', { static: true }) paymentAccountCellTpl: TemplateRef<unknown>;
  @ViewChild('amountCellTpl', { static: true }) amountCellTpl: TemplateRef<unknown>;
  @ViewChild('paymentDateCellTpl', { static: true }) paymentDateCellTpl: TemplateRef<unknown>;
  @ViewChild('editCellTpl', { static: true }) editCellTpl: TemplateRef<unknown>;
  @ViewChild(AutoPaySetupWrapperComponent) childComponent: AutoPaySetupWrapperComponent;

  loading$: Observable<boolean>;

  autoPayEligible$: Observable<boolean>;

  paymentMethods$: Observable<PaymentMethod[]>;
  automaticPayments$: Observable<AutomaticPayment[]>;

  tableRows$: Observable<AutoPayTableRow[]>;
  tableColumns: DatatableColumn[];

  noAutoPaySetupContentPath$: Observable<string>;

  billAccountToDelete: string;
  displayDeleteAutoPayDiscountMessage$: Observable<boolean>;

  newBillingDesign = false;
  autoPayAccounts;
  paymentMethods;
  autopayEnrolledAccount;
  autopayNotEnrolledAccount;
  autopayAccountsList = { notEnrolledAccounts: [], enrolledAccounts: [] };
  selectedPaymentMethod = [];
  editEnabledFor = '';
  selectedBillAccount = '';
  loadingBillingDetails$: Observable<boolean>;
  hasError$: Observable<boolean>;
  paymentMethodAddEditConfig$: BehaviorSubject<PaymentMethodAddEditConfig> = new BehaviorSubject(
    null
  );

  private stop$: Subject<void> = new Subject<void>();
  private isHSPolicy = false;
  constructor(
    private modalService: DsModalService,
    private store: Store,
    private autoPayUtil: AutoPayUtilService,
    private analyticsFacade: AnalyticsFacade,
    private autoPayDeleteUtil: AutoPayDeleteUtilService,
    private feature: FeatureFlagService,
    private toaster: ToasterService,
    public spinner: LoadingSpinnerService,
    private utilService: UtilService
  ) {}

  ngOnInit(): void {
    this.newBillingDesign = this.feature.isEnabled('new-billing-design');
    this.initAutoPayDetail();
    if (this.newBillingDesign) {
      this.setupAccountDetails();
    }
    this.store
      .select(BillAccountsSelectors.selectIsSelectedBillPolicyHomesite)
      .pipe(takeUntil(this.stop$))
      .subscribe(isHSPolicy => {
        this.isHSPolicy = isHSPolicy;
      });
  }

  resetAutoPayForm() {
    if (this.childComponent) {
      this.childComponent.resetForm();
    }
  }
  // when routing directly between detail screens
  ngOnChanges(): void {
    this.initAutoPayDetail();
  }

  setupAutoPay() {
    this.analyticsFacade.trackButtonClick(MultiAutopaySetupAnalytics.addMultiAutopaySetup);

    // AUTOPAY ENTRY POINT
    if (this.billAccountNumber) {
      this.autoPayUtil.routeToMultipleAutoPaySelection(`/billing/${this.billAccountNumber}`, [
        this.billAccountNumber
      ]);
    } else {
      this.autoPayUtil.routeToMultipleAutoPaySelection('/billing');
    }
  }

  billAccountClick(billAccountNumber: string) {
    this.store.dispatch(
      fromRouterActions.Go({
        path: [BillingPaymentPaths.BILLING_DETAIL_PAGE, billAccountNumber]
      })
    );
  }

  editAutoPayClick(billAccountNumber: string) {
    // AUTOPAY ENTRY POINT
    this.autoPayUtil.editAutoPay(billAccountNumber, `/billing/${billAccountNumber}`);
  }

  deleteAutoPayClick(billAccountNumber: string): void {
    this.billAccountToDelete = billAccountNumber;

    this.displayDeleteAutoPayDiscountMessage$ = this.store.select(
      AutoPayUtilSelectors.selectBillAccountHasAutoPayDiscount(billAccountNumber)
    );

    this.modalService.open('autoPayDeleteModal');
    this.analyticsFacade.trackPage(
      AutomaticPaymentsAnalytics.MyAccountBillingPaymentsMultipleAutomaticPaymentsEditDeleteStart
    );
  }

  deleteAutoPayConfirm(): void {
    this.modalService.close('autoPayDeleteModal');
    const billAccountNumberToDelete: string = this.billAccountNumber
      ? this.billAccountNumber
      : this.billAccountToDelete;
    this.store
      .select(AutoPaySelectors.getAutomaticPayment(billAccountNumberToDelete))
      .pipe(take(1))
      .subscribe(automaticPayment => {
        this.autoPayDeleteUtil.deleteAutoPay(automaticPayment);
      });
  }

  setupAccountDetails() {
    this.loadingBillingDetails$ = this.store.select(
      BillAccountsSelectors.selectBillAccountsLoading
    );

    combineLatest([
      this.store.select(BillAccountsSelectors.selectBillAccounts),
      this.automaticPayments$,
      this.store.select(PaymentMethodSelectors.selectPaymentMethods)
    ])
      .pipe(
        map(([billAccounts, automaticPayments, paymentMethods]) => ({
          billAccounts,
          automaticPayments,
          paymentMethods
        })),
        filter(state => state.billAccounts?.length > 0),
        takeUntil(this.stop$)
      )
      .subscribe(state => {
        if (state.automaticPayments) {
          this.autoPayAccounts = state.automaticPayments;
        }

        if (state.paymentMethods) {
          this.paymentMethods = state.paymentMethods;
        }

        this.autopayEnrolledAccount = [];
        this.autopayNotEnrolledAccount = [];
        this.autopayAccountsList.enrolledAccounts = [];
        this.autopayAccountsList.notEnrolledAccounts = [];
        _forEach(state.billAccounts, (billAccount: BillAccount) => {
          if (
            _has(billAccount, 'billAccountNumber') &&
            (_has(billAccount, 'policyList') || _has(billAccount, 'policyTypeDisplayName'))
          ) {
            this.setAutoPayList(billAccount);
          }
        });
        if (this.autopayNotEnrolledAccount.length) {
          this.autopayAccountsList.notEnrolledAccounts = this.autopayNotEnrolledAccount;
        }

        if (this.autopayEnrolledAccount.length) {
          this.autopayAccountsList.enrolledAccounts = this.autopayEnrolledAccount;
        }
      });
  }

  setAutoPayList(billAccount) {
    const billAcc = {
      isAutoPayEnabled: this.isAutoPayEnrolled(billAccount),
      accountNumber: billAccount.billAccountNumber,
      riskDesc: this.getRiskDesc(billAccount),
      enableEdit: false,
      automaticPayment: this.getAutomaticPayemntDetails(billAccount),
      paymentMethod: this.selectedPaymentMethod
    };

    if (billAcc.isAutoPayEnabled) {
      this.autopayEnrolledAccount.push(billAcc);
    } else {
      this.autopayNotEnrolledAccount.push(billAcc);
    }
  }

  isAutoPayEnrolled(billAccount) {
    const billAccountHasAutoPay: boolean = this.autoPayAccounts
      .map(autoPay => autoPay?.billAccountNumber)
      .includes(billAccount.billAccountNumber);
    return billAccountHasAutoPay;
  }

  getRiskDesc(billAccount) {
    const risk = [];
    if (billAccount.policyList?.length && billAccount.policyList[0].riskDescriptionList) {
      risk.push(billAccount.policyList[0].riskDescriptionList);
    } else if (billAccount.policyTypeDisplayName) {
      risk.push(billAccount.policyTypeDisplayName);
    }
    return risk;
  }

  getAutomaticPayemntDetails(billAccount) {
    let autoPayDetails = null;
    if (this.autoPayAccounts) {
      autoPayDetails = this.autoPayAccounts.filter(
        account => account?.billAccountNumber === billAccount?.billAccountNumber
      );
    }
    if (autoPayDetails.length) {
      this.getPaymentMethod(autoPayDetails);
    }
    return autoPayDetails;
  }

  getPaymentMethod(autopayDetails) {
    let payMethod = null;
    if (this.paymentMethods) {
      payMethod = this.paymentMethods.filter(
        method => method?.nickName === autopayDetails[0].paymentAccount.nickName
      );
    }
    this.selectedPaymentMethod = payMethod;
  }

  editAutoPayClickToggle(billAccountNumber: string) {
    _forEach(this.autopayAccountsList.enrolledAccounts, enrolledAccounts => {
      if (enrolledAccounts.accountNumber === billAccountNumber) {
        enrolledAccounts.enableEdit = !enrolledAccounts.enableEdit;
      }
    });
  }

  editpaymentMethod(accountNumber: string, editEnabledFor: string) {
    this.editEnabledFor = editEnabledFor;
    this.selectedBillAccount = accountNumber;
    this.modalService.open('autopayEditModalId');
    this.autoPayUtil.editAutoPayment(this.selectedBillAccount);
  }

  corfirmSubmissionStatus(status) {
    let editedPay = '';
    if (this.editEnabledFor === 'paymentMethod') {
      editedPay = 'Payment Method';
    } else if (this.editEnabledFor === 'paymentDate') {
      editedPay = 'Payment Date';
    }
    if (status === 'SUCCESS') {
      this.toaster.pop('success', editedPay + ' Updated');
    } else if (status === 'FAILURE' || status === 'PARTIAL_FAILURE') {
      this.toaster.pop('error', editedPay + ' Failed');
    }
  }

  deleteAlertPopup(billAccountNumber) {
    this.modalService.open('deleteAutopayModalId');
    this.billAccountToDelete = billAccountNumber;
    this.displayDeleteAutoPayDiscountMessage$ = this.store.select(
      AutoPayUtilSelectors.selectBillAccountHasAutoPayDiscount(billAccountNumber)
    );
  }

  deleteAutoPayConfirmed() {
    this.modalService.close('deleteAutopayModalId');

    this.store
      .select(AutoPaySelectors.getAutomaticPayment(this.billAccountToDelete))
      .pipe(take(1))
      .subscribe((automaticPayment: AutomaticPayment) => {
        this.autoPayDeleteUtil.deleteAutoPayUtil(automaticPayment);
      });
  }

  closeDeleteAutopayModal() {
    this.modalService.close('deleteAutopayModalId');
  }

  setUpAutopay(billAccountNumber: string) {
    this.autoPayUtil.routeToMultipleAutoPaySelection(`/billing/${billAccountNumber}`, [
      billAccountNumber
    ]);
    this.autoPayUtil.addAutoPay(billAccountNumber, 'billing/manage-autopay', this.isHSPolicy);
  }

  ngOnDestroy() {
    this.stop$.next();
    this.stop$.complete();
  }

  addCheckingAccount(): void {
    this.openPaymentMethodModal({
      accountType: PaymentMethodAccountType.CHECKING,
      operation: PaymentMethodAddEditOperationType.ADD,
      paymentMethod: null
    });
  }

  addCreditDebit(): void {
    this.openPaymentMethodModal({
      accountType: PaymentMethodAccountType.CREDIT_DEBIT,
      operation: PaymentMethodAddEditOperationType.ADD,
      paymentMethod: null
    });
  }

  paymentMethodAddEditComplete(newPaymentAccountId: string): void {
    this.modalService.close('paymentMethodModal');

    const correlationId: string = this.utilService.generateId();
    this.spinner.start({
      blockActions: true
    });
    this.store.dispatch(PaymentMethodActions.getPaymentMethods({ correlationId }));
    this.store
      .select(PaymentMethodSelectors.selectPaymentMethodState)
      .pipe(
        filter(state => state.correlationId === correlationId),
        take(1)
      )
      .subscribe(state => {
        this.spinner.stop();
        if (!state.hasError) {
          const newPaymentMethodOption = state.entities[newPaymentAccountId];
          console.log('kajal newPaymentMethodOption:', newPaymentMethodOption);
          if (this.isHSPolicy) {
            this.modalService.closeAll();
            this.modalService.open('addPaymentMethodSuccessModal');
          }
        }
      });
  }

  private openPaymentMethodModal(modalConfig: PaymentMethodAddEditConfig) {
    this.paymentMethodAddEditConfig$.next(modalConfig);
    this.modalService.open('paymentMethodModal', true);
  }

  private initAutoPayDetail(): void {
    this.autoPayEligible$ = this.store.select(
      AutoPayUtilSelectors.selectBillAccountsEligibleForAutoPayHasLength
    );
    this.loading$ = this.store.select(AutoPaySelectors.getAnyLoading);
    this.paymentMethods$ = this.store.select(PaymentMethodSelectors.selectPaymentMethods);
    this.automaticPayments$ = this.getAutomaticPayments();
    this.tableColumns = this.getTableColumns();
    this.tableRows$ = this.getTableRows();
    this.noAutoPaySetupContentPath$ = this.getNoAutoPaySetupContentPath();
  }

  private getAutomaticPayments(): Observable<AutomaticPayment[]> {
    return this.store
      .select(AutoPaySelectors.getAutomaticPayments)
      .pipe(
        map(automaticPayments =>
          this.billAccountNumber
            ? automaticPayments.filter(
                automaticPayment => automaticPayment.billAccountNumber === this.billAccountNumber
              )
            : automaticPayments.filter(payment => payment !== undefined)
        )
      );
  }

  private getTableRows(): Observable<AutoPayTableRow[]> {
    return combineLatest([this.automaticPayments$, this.paymentMethods$]).pipe(
      map(([automaticPayments, paymentMethods]) =>
        automaticPayments.map((automaticPayment: AutomaticPayment) => {
          const billAccountNumber: string = automaticPayment?.billAccountNumber;
          const paymentMethod: PaymentMethod = paymentMethods.find(
            payment => payment.nickName === automaticPayment?.paymentAccount.nickName
          );
          const paymentDate: string = this.getPaymentDateText(automaticPayment);
          const paymentAmount: string = this.getPaymentAmountText(automaticPayment);
          return {
            billAccountNumber,
            paymentMethod,
            paymentDate,
            paymentAmount
          };
        })
      )
    );
  }

  private getNoAutoPaySetupContentPath(): Observable<string> {
    return this.store.select(PolicySelectors.selectNoAutoPaySetupContentPath);
  }

  private getPaymentDateText(automaticPayment: AutomaticPayment): string {
    const validDate: boolean =
      isValid(new Date(automaticPayment?.nextPaymentDate)) &&
      automaticPayment.predictedDollarAmount !== 'UNKNOWN';
    return validDate
      ? new DateFormatPipe().transform(automaticPayment.nextPaymentDate, 'MM/DD/YYYY')
      : parseInt(automaticPayment?.daysBeforeDueDate, 10) === 0
        ? 'Paid on due date'
        : parseInt(automaticPayment?.daysBeforeDueDate, 10) === 1
          ? '1 day before due date'
          : parseInt(automaticPayment?.daysBeforeDueDate, 10) > 1
            ? `${automaticPayment.daysBeforeDueDate} days before due date`
            : '';
  }

  private getPaymentAmountText(automaticPayment: AutomaticPayment): string {
    const validAmount: boolean = automaticPayment?.predictedDollarAmount !== 'UNKNOWN';
    return validAmount
      ? new CurrencyUSDPipe().transform(automaticPayment?.predictedDollarAmount)
      : automaticPayment.paymentAmount === 'MinDue'
        ? 'Minimum Due'
        : automaticPayment.paymentAmount === 'FullPay'
          ? 'Account Balance'
          : '';
  }

  private getTableColumns(): DatatableColumn[] {
    const tableColumns: DatatableColumn[] = [
      {
        cellTemplate: this.billAccountNameCellTpl,
        headerTemplate: this.headerCellTpl,
        isHeaderOnMobile: true,
        name: 'Billing Account',
        shortName: 'Account',
        prop: 'billAccount',
        draggable: false,
        resizeable: false,
        canAutoResize: true,
        width: 140
      },
      {
        cellTemplate: this.paymentAccountCellTpl,
        headerTemplate: this.headerCellTpl,
        name: 'Method',
        shortName: 'Method',
        prop: 'paymentMethod',
        draggable: false,
        resizeable: false,
        canAutoResize: true,
        width: 200
      },
      {
        cellTemplate: this.amountCellTpl,
        headerTemplate: this.headerCellTpl,
        name: 'Pay',
        shortName: 'Pay',
        prop: 'amount',
        draggable: false,
        resizeable: false,
        canAutoResize: true,
        width: 120
      },
      {
        cellTemplate: this.paymentDateCellTpl,
        headerTemplate: this.headerCellTpl,
        name: 'When',
        shortName: 'When',
        prop: 'paymentDate',
        draggable: false,
        resizeable: false,
        canAutoResize: true,
        width: 120
      }
    ];

    if (!this.tableOnly) {
      tableColumns.push({
        cellTemplate: this.editCellTpl,
        headerTemplate: this.headerCellTpl,
        name: ' ',
        shortName: '',
        prop: 'editDelete',
        draggable: false,
        resizeable: false,
        sortable: false,
        canAutoResize: false,
        width: 180
      });
    }

    return tableColumns;
  }
}
